import { jwtDecode } from 'jwt-decode';
import { User, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { createSpoofingNotAllowedError } from '../errors';
import SessionStore from '../legacy/SessionStore';
import UserService from '../legacy/UserService';
import { clearAuthProvider, rememberAuthProvider } from '../provider';
import { validateAdminRole } from '../roleValidation';
import type {
  AuthAccessTokenOptions,
  AuthAdapter,
  AuthImpersonationRequest,
  AuthLogoutOptions,
  AuthNotice,
  AuthStartLoginOptions,
  AuthState,
} from '../types';

import * as browserLocation from './browserLocation';

const authNoticeStorageKey = 'authNotice';
const postLoginRedirectStorageKey = 'authV2PostLoginRedirect';
const tokenStorageKey = 'token';
const defaultClientId = 'admin-portal';
const oidcScope = 'openid offline_access';
const tokenExchangeGrantType = 'urn:ietf:params:oauth:grant-type:token-exchange';
const accountIdSubjectTokenType = 'urn:ietf:params:oauth:token-type:luxury-escapes:account-id';
const jwtActorTokenType = 'urn:ietf:params:oauth:token-type:jwt';
const tokenFreshnessBufferSeconds = 60;
const refreshLockName = 'auth-v2-oidc-refresh';

type OidcClaims = Record<string, unknown>;

export class AuthV2Adapter implements AuthAdapter {
  private initialized = false;

  private listeners = new Set<() => void>();

  private readonly sessionStore: SessionStore;

  private readonly userService: UserService;

  private readonly userManager: UserManager;

  private sessionError: Error | null = null;

  private pendingOidcLogin: Promise<void> | null = null;

  private pendingOidcTokenRefresh: Promise<string | null> | null = null;

  private state: AuthState;

  constructor() {
    this.sessionStore = new SessionStore();
    this.userService = new UserService({ baseUri: window.configs.API_HOST }, false);
    this.userManager = AuthV2Adapter.createUserManager();
    this.state = this.computeState();

    this.sessionStore.addChangeListener(() => {
      this.notify();
    });
  }

  initialize() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    window.addEventListener('storage', this.handleStorageChange);
    void this.initializeSession().catch((error) => {
      this.handleInitializationFailure(error);
    });
  }

  // storage events fire only in tabs other than the one that made the change, so reacting to the
  // mirrored access-token key keeps this tab in sync when another tab logs in, logs out, or rotates
  // the token. A key of null means localStorage was cleared wholesale, which we also honour.
  private readonly handleStorageChange = (event: StorageEvent) => {
    if (event.key !== null && event.key !== tokenStorageKey) {
      return;
    }

    void this.resolveSession(true);
  };

  subscribe(listener: () => void) {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): AuthState {
    return this.state;
  }

  static async redirectToLogin(returnTo = browserLocation.getWindowHref()) {
    const { postLoginRedirectPath, redirectUri } = AuthV2Adapter.normalizeReturnTo(returnTo);

    rememberAuthProvider('v2');

    if (postLoginRedirectPath) {
      localStorage.setItem(postLoginRedirectStorageKey, postLoginRedirectPath);
    } else {
      localStorage.removeItem(postLoginRedirectStorageKey);
    }

    await AuthV2Adapter.createUserManager().signinRedirect({
      redirect_uri: redirectUri,
    });
  }

  async startLogin(options?: AuthStartLoginOptions): Promise<void> {
    await AuthV2Adapter.redirectToLogin(options?.returnTo);
  }

  async completeLoginIfNeeded(): Promise<void> {
    if (!this.hasOidcCallbackParams()) {
      return;
    }

    await this.completeOidcLogin();
  }

  async getAccessToken(options?: AuthAccessTokenOptions): Promise<string | null> {
    return this.getV2AccessToken(false, options);
  }

  async getFreshAccessToken(options?: AuthAccessTokenOptions): Promise<string | null> {
    return this.getV2AccessToken(true, options);
  }

  async logout(options?: AuthLogoutOptions): Promise<void> {
    if (options?.reason === 'session-expired') {
      localStorage.setItem(authNoticeStorageKey, options.reason);
    }

    // Local cleanup must complete before signoutRedirect navigates the page away so the
    // writes are flushed on unload. The stored OIDC user is left in place so signoutRedirect
    // can use its id_token as the id_token_hint; it clears the user as part of the flow.
    this.resetSession();
    this.userService.delToken();
    clearAuthProvider();
    this.notify();

    // Terminate the upstream IdP session so a subsequent login requires fresh authentication
    // rather than silently resuming via SSO. Fall back to a local user clear if the redirect
    // cannot be initiated.
    await this.userManager.signoutRedirect().catch(async () => {
      await this.userManager.removeUser().catch(() => undefined);
    });
  }

  isAuthenticated() {
    return !!this.sessionStore.get()?.account;
  }

  async refreshSession(): Promise<void> {
    await this.resolveSession(true);
  }

  consumeNotice(): AuthNotice | null {
    const notice = localStorage.getItem(authNoticeStorageKey);
    localStorage.removeItem(authNoticeStorageKey);

    if (notice === 'session-expired') {
      return { type: 'session-expired' };
    }

    return null;
  }

  private async resolveSession(force = false): Promise<void> {
    if (!force && (this.sessionError || !this.sessionStore.empty())) {
      return;
    }

    this.sessionStore.set({
      loading: true,
      account: null,
    });

    const accessToken = await this.getV2AccessToken(false);

    if (!accessToken) {
      this.sessionError = null;
      if (!(await this.canResolveOidcSession())) {
        this.userService.delToken();
      }
      this.sessionStore.set({
        loading: false,
        account: null,
      });
      this.notify();
      return;
    }

    const fallbackAccount = await this.getOidcAccountFallback(accessToken);

    await new Promise<void>((done) => {
      this.userService.me((error, account) => {
        // Only fall back to token claims when /me is unavailable (transport error) and the
        // backend never returned an account. A definitive non-admin /me response is
        // authoritative and must never be overridden by client-side decoded token claims.
        const meUnavailable = !!error;
        const shouldUseFallbackAccount = meUnavailable && !!fallbackAccount && !validateAdminRole(fallbackAccount);
        const accountSessionError = error ?? validateAdminRole(account);
        const resolvedAccount = shouldUseFallbackAccount ? fallbackAccount : account;
        const sessionError = shouldUseFallbackAccount ? null : accountSessionError;

        if (sessionError) {
          console.warn('Auth V2 session resolution failed after token acquisition.', sessionError);
          this.sessionError = sessionError;
          this.userService.delToken();
          this.sessionStore.set({
            loading: false,
            account: null,
          });
        } else {
          if (this.restorePostLoginRedirect()) {
            done();
            return;
          }

          this.sessionError = null;
          this.sessionStore.set({
            loading: false,
            account: resolvedAccount,
          });
        }

        done();
      });
    });
  }

  private async initializeSession() {
    if (this.hasOidcCallbackParams()) {
      await this.completeOidcLogin();
      return;
    }

    if (this.hasOidcSignoutParams()) {
      await this.completeOidcSignout();
    }

    await this.resolveSession(false);
  }

  private handleInitializationFailure(error: unknown) {
    console.warn('Auth V2 initialization failed.', error);
    if (this.hasOidcCallbackParams()) {
      this.cleanOidcCallbackParams();
    }
    this.sessionError = error instanceof Error ? error : new Error('Auth V2 initialization failed.');
    this.userService.delToken();
    this.sessionStore.set({
      loading: false,
      account: null,
    });
    this.notify();
  }

  private async completeOidcSignout() {
    // The IdP appends ?state=... when redirecting back from its end-session endpoint.
    // Only strip it once oidc-client-ts confirms it matches a sign-out we initiated;
    // otherwise the param belongs to the app and must be left untouched.
    try {
      await this.userManager.signoutRedirectCallback(browserLocation.getWindowHref());
    } catch (_error) {
      return;
    }

    this.cleanOidcCallbackParams();
  }

  private async completeOidcLogin() {
    // The authorization code and state are single-use. initializeSession() and
    // completeLoginIfNeeded() can both reach here on the same page load, so share one
    // in-flight promise to guarantee signinRedirectCallback runs exactly once.
    if (!this.pendingOidcLogin) {
      this.pendingOidcLogin = this.runOidcLogin().finally(() => {
        this.pendingOidcLogin = null;
      });
    }

    return this.pendingOidcLogin;
  }

  private async runOidcLogin() {
    const user = await this.userManager.signinRedirectCallback(browserLocation.getWindowHref());
    rememberAuthProvider('v2');
    this.storeOidcUser(user);
    this.cleanOidcCallbackParams();
    await this.resolveSession(true);
  }

  private async getV2AccessToken(forceRefresh: boolean, options?: AuthAccessTokenOptions): Promise<string | null> {
    if (options?.impersonation) {
      return this.exchangeImpersonationAccessToken(options.impersonation);
    }

    return this.getOidcAccessToken(forceRefresh);
  }

  private async exchangeImpersonationAccessToken(impersonation: AuthImpersonationRequest) {
    const actorToken = await this.getV2AccessToken(false);

    if (!actorToken) {
      return null;
    }

    return this.exchangeImpersonationToken(impersonation, actorToken);
  }

  private async getOidcAccessToken(forceRefresh: boolean): Promise<string | null> {
    const user = await this.userManager.getUser().catch(() => null);

    if (!forceRefresh && user && this.isOidcUserFresh(user)) {
      this.storeOidcUser(user);
      return user.access_token;
    }

    // Silent renew relies on the refresh token (offline_access). Without one, oidc-client-ts
    // would fall back to a hidden iframe loading the full app at the origin with nothing to
    // post the result back, so the renew would hang until timeout. Bail out instead.
    if (!user?.refresh_token) {
      return null;
    }

    if (!this.pendingOidcTokenRefresh) {
      this.pendingOidcTokenRefresh = this.refreshOidcAccessToken(user.refresh_token).finally(() => {
        this.pendingOidcTokenRefresh = null;
      });
    }

    return this.pendingOidcTokenRefresh;
  }

  private isOidcUserFresh(user: User): boolean {
    if (!user.access_token || user.expired) {
      return false;
    }

    // Refresh slightly before the hard expiry so a near-expiry token isn't handed to a caller
    // and then rejected mid-request by the API once it lapses in flight.
    return user.expires_in == null || user.expires_in > tokenFreshnessBufferSeconds;
  }

  private async refreshOidcAccessToken(staleRefreshToken: string): Promise<string | null> {
    return this.withRefreshLock(async () => {
      // The auth server rotates refresh tokens and detects reuse, so two tabs refreshing with the
      // same token would have the second request rejected — and could get the whole token family
      // revoked. While we waited for the lock another tab may have already rotated it, so re-read
      // the shared store: if the refresh token has changed and the stored token is fresh, reuse it
      // instead of spending our now-consumed token on a second refresh.
      const current = await this.userManager.getUser().catch(() => null);

      if (current?.access_token && current.refresh_token !== staleRefreshToken && this.isOidcUserFresh(current)) {
        this.storeOidcUser(current);
        return current.access_token;
      }

      const refreshedUser = await this.userManager.signinSilent().catch(() => null);

      if (!refreshedUser?.access_token) {
        return null;
      }

      this.storeOidcUser(refreshedUser);
      return refreshedUser.access_token;
    });
  }

  private async withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
    const lockManager = (navigator as { locks?: LockManager }).locks;

    // Web Locks are origin-scoped, so a single named lock serializes refreshes across every tab.
    // Fall back to running unlocked where the API is unavailable (older browsers, tests); the
    // per-tab in-flight promise still prevents duplicate refreshes within this tab.
    if (!lockManager) {
      return task();
    }

    return lockManager.request(refreshLockName, task);
  }

  private async canResolveOidcSession() {
    const user = await this.userManager.getUser().catch(() => null);

    return !!user?.access_token && !!user.refresh_token;
  }

  private async exchangeImpersonationToken(
    impersonation: AuthImpersonationRequest,
    actorToken: string,
  ): Promise<string | null> {
    let response: Response;

    try {
      response = await fetch(AuthV2Adapter.getAuthServiceUrl('/api/auth/v2/token'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: this.toFormBody(impersonation, actorToken),
      });
    } catch (networkError) {
      // Surface a transient network failure as an error without touching session state, so the
      // existing admin session survives the blip and the caller can retry.
      throw new Error('Failed to obtain impersonation token', { cause: networkError });
    }

    const body = await response.json().catch(() => ({}));
    const accessToken = body?.access_token ?? body?.result?.access_token ?? null;

    if (response.ok && accessToken) {
      return accessToken;
    }

    // 403 on an impersonation exchange means the admin is not permitted to spoof this user.
    // Surface it as a targeted error and keep the admin session intact.
    if (impersonation && response.status === 403) {
      throw createSpoofingNotAllowedError();
    }

    // 401 is the only status that indicates a revoked session cookie. Every other
    // failure (403, 5xx, malformed body) is a request-level error and must not
    // destroy the existing admin session.
    if (response.status === 401 && this.userService.getToken()) {
      await this.logout({ reason: 'session-expired' });
      return null;
    }

    throw new Error(body?.message || body?.error || 'Failed to obtain impersonation token');
  }

  private toFormBody(impersonation: AuthImpersonationRequest, actorToken: string) {
    const searchParams = new URLSearchParams();

    searchParams.set('grant_type', tokenExchangeGrantType);
    searchParams.set('subject_token_type', accountIdSubjectTokenType);
    searchParams.set('subject_token', impersonation.customerId);
    searchParams.set('actor_token_type', jwtActorTokenType);
    searchParams.set('actor_token', actorToken);

    return searchParams.toString();
  }

  private async getOidcAccountFallback(accessToken: string): Promise<App.User | null> {
    const claims = await this.getOidcClaims(accessToken);
    const roles = this.getClaimRoles(claims);

    if (!roles.length) {
      return null;
    }

    const email = this.getStringClaim(claims, 'email');
    const givenName = this.getStringClaim(claims, 'given_name') ?? this.getStringClaim(claims, 'givenName') ?? '';
    const surname = this.getStringClaim(claims, 'family_name') ?? this.getStringClaim(claims, 'surname') ?? '';
    const fullName = this.getStringClaim(claims, 'name') ?? [givenName, surname].filter(Boolean).join(' ');

    return {
      email: email ?? '',
      email_verified: this.getBooleanClaim(claims, 'email_verified') ?? false,
      fullName,
      givenName,
      id_member: this.getStringClaim(claims, 'sub') ?? '',
      memberId: this.getStringClaim(claims, 'sub') ?? '',
      roles,
      surname,
    } as App.User;
  }

  private async getOidcClaims(accessToken: string): Promise<OidcClaims> {
    const user = await this.userManager.getUser().catch(() => null);

    return {
      ...(user?.profile as OidcClaims | undefined),
      ...this.decodeTokenClaims(accessToken),
    };
  }

  private decodeTokenClaims(token: string): OidcClaims {
    try {
      return jwtDecode<OidcClaims>(token);
    } catch (_error) {
      return {};
    }
  }

  private getClaimRoles(claims: OidcClaims): Array<string> {
    return [
      ...this.getStringArrayClaim(claims, 'roles'),
      ...this.getStringArrayClaim(claims, 'role'),
      ...this.getStringArrayClaim(claims, 'cognito:groups'),
      ...this.getStringArrayClaim(claims, 'custom:roles'),
    ];
  }

  private getStringArrayClaim(claims: OidcClaims, key: string): Array<string> {
    const value = claims[key];

    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }

    if (typeof value === 'string') {
      return value.split(/[,\s]+/).filter(Boolean);
    }

    return [];
  }

  private getStringClaim(claims: OidcClaims, key: string) {
    const value = claims[key];

    return typeof value === 'string' ? value : null;
  }

  private getBooleanClaim(claims: OidcClaims, key: string) {
    const value = claims[key];

    return typeof value === 'boolean' ? value : null;
  }

  private storeOidcUser(user: User) {
    if (this.userService.getToken() === user.access_token) {
      return;
    }

    this.userService.setToken(user.access_token);
    this.notify();
  }

  private hasOidcCallbackParams() {
    const windowParams = new URLSearchParams(window.location.search);

    // A successful authorization response carries `code`; a failed one (e.g. access_denied)
    // carries `error` instead. Both are sign-in callbacks and must reach signinRedirectCallback,
    // which validates the state and surfaces the error rather than silently swallowing it.
    return !!windowParams.get('state') && (!!windowParams.get('code') || !!windowParams.get('error'));
  }

  private hasOidcSignoutParams() {
    const windowParams = new URLSearchParams(window.location.search);

    return !!windowParams.get('state') && !windowParams.get('code') && !windowParams.get('error');
  }

  private cleanOidcCallbackParams() {
    const windowParams = new URLSearchParams(window.location.search);

    windowParams.delete('code');
    windowParams.delete('state');
    windowParams.delete('session_state');
    windowParams.delete('error');
    windowParams.delete('error_description');

    const cleanSearch = windowParams.toString();
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${window.location.hash}`,
    );
  }

  private static createUserManager() {
    const origin = browserLocation.getWindowOrigin();

    return new UserManager({
      authority: AuthV2Adapter.getAuthServiceUrl('/api/auth/v2'),
      client_id: AuthV2Adapter.getClientId(),
      scope: oidcScope,
      response_type: 'code',
      redirect_uri: origin,
      post_logout_redirect_uri: origin,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: false,
      monitorSession: false,
    });
  }

  private static getAuthServiceUrl(path: string) {
    return new URL(path, window.configs.AUTH_HOST).toString();
  }

  private static getClientId() {
    return window.configs.AUTH_CLIENT_ID?.trim() || defaultClientId;
  }

  private static normalizeReturnTo(returnTo: string) {
    // The auth service is responsible for allowlisting redirect origins, but we defensively
    // clamp redirect_uri to the current origin so a caller can never steer the redirect off-site.
    const origin = browserLocation.getWindowOrigin();

    try {
      const returnToUrl = new URL(returnTo, origin);
      const postLoginRedirectPath =
        returnToUrl.origin === origin ? `${returnToUrl.pathname}${returnToUrl.search}${returnToUrl.hash}` : null;

      return {
        redirectUri: origin,
        postLoginRedirectPath: postLoginRedirectPath && postLoginRedirectPath !== '/' ? postLoginRedirectPath : null,
      };
    } catch (_error) {
      return {
        redirectUri: origin,
        postLoginRedirectPath: null,
      };
    }
  }

  private restorePostLoginRedirect() {
    const redirectPath = localStorage.getItem(postLoginRedirectStorageKey);

    if (!redirectPath) {
      return false;
    }

    localStorage.removeItem(postLoginRedirectStorageKey);

    const currentPath = browserLocation.getCurrentPath();

    if (currentPath !== '/' || redirectPath === currentPath || !redirectPath.startsWith('/')) {
      return false;
    }

    browserLocation.replaceLocation(redirectPath);
    return true;
  }

  private resetSession() {
    this.sessionError = null;
    this.sessionStore.set({
      loading: false,
      account: null,
    });
  }

  private notify() {
    this.state = this.computeState();
    this.listeners.forEach((listener) => listener());
  }

  private computeState(): AuthState {
    const session = this.sessionStore.get();
    const token = this.userService.getToken();

    return {
      loading: session?.loading ?? true,
      authenticated: !!session?.account,
      token,
      user: session?.account ?? null,
    };
  }
}
